/**
 * @fileoverview ImportSuggestion — "No match" helper text for entity lookups.
 *
 * Shows a single line of muted text when the import has a name but no
 * fuzzy match was found. Auto-select is handled by the step via useEntityMatch.
 *
 * Usage:
 *   <ImportSuggestion importedName="John Smith" matched={false} />
 */
import { Sparkles } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

export interface ImportSuggestionProps {
  /** The raw name string from the CMS import */
  importedName: string | null | undefined;
  /** Whether a match was found and auto-selected */
  matched: boolean;
  /** The currently selected value ID — hides when user has made a selection */
  currentValueId?: string | null;
  className?: string;
}

export const ImportSuggestion: React.FC<ImportSuggestionProps> = ({
  importedName,
  matched,
  currentValueId,
  className,
}) => {
  // Don't show if: no imported name, match was found, or user already selected
  if (!importedName?.trim()) return null;
  if (matched) return null;
  if (currentValueId) return null;

  return (
    <p
      className={cn(
        'flex items-center gap-1 text-[11px] text-zinc-400 mt-1',
        className,
      )}
    >
      <Sparkles className="h-3 w-3 shrink-0 text-zinc-300" />
      <span>
        Imported <span className="font-medium text-zinc-500">&ldquo;{importedName}&rdquo;</span>
        {' — '}no match found
      </span>
    </p>
  );
};
